import { MatterDto, MatterEmployeeRoleType } from 'types/matters';
import { ReferralTypeCode } from 'types/referrals';
import { t } from 'utils/getTranslations';
import { NamedEntityParts } from 'utils/nameOrder';
import { formatSubstitutePeriodSuffix } from './util';

interface TeamMember {
    employee: NamedEntityParts & { id: number };
    roles: string[];
}

/**
 * Everyone the classic Our team rows name, one entry per person, holding their roles. People and roles follow the order
 * supervising partner, its substitute, responsible lawyer, its substitute, referred by, then the STAFF rows sorted by
 * name in one pass. With the case assistants told apart by `caseAssistantLabel`, Handled by and Case assistant therefore
 * interleave by name, where the classic rows list every case assistant first. A label the person already holds is not
 * repeated: with no label, two STAFF rows for one person both read Handled by.
 */
export const teamMembers = (
    matter: MatterDto,
    caseAssistantLabel: string | null,
    dateFormat?: string
): TeamMember[] => {
    const members = new Map<number, TeamMember>();
    const add = (employee: TeamMember['employee'] | undefined, role: string) => {
        if (!employee?.id) return;
        const member = members.get(employee.id);
        if (!member) members.set(employee.id, { employee, roles: [role] });
        else if (!member.roles.includes(role)) member.roles.push(role);
    };
    const substituteRole = (labelId: string, { startDate, endDate }: { startDate: string; endDate: string }) =>
        `${t(labelId)} ${formatSubstitutePeriodSuffix(startDate, endDate, dateFormat)}`;
    const { substituteSupervisingPartner: substituteSp, substituteResponsibleLawyer: substituteRl } = matter;

    add(matter.supervisedByEmployee, t('matters.supervising-partner'));
    if (substituteSp) {
        add(substituteSp.employee, substituteRole('matters.substitute-supervising-partner', substituteSp));
    }
    add(matter.responsibleEmployee, t('matters.responsible-lawyer'));
    if (substituteRl) {
        add(substituteRl.employee, substituteRole('matters.substitute-responsible-lawyer', substituteRl));
    }
    const referrers = (matter.referrals ?? []).flatMap((r) =>
        r.referralTypeCode === ReferralTypeCode.EMPLOYEE && r.employee ? [r.employee] : []
    );
    const referredBy = referrers.length > 0 ? referrers : matter.referredByEmployee ? [matter.referredByEmployee] : [];
    referredBy.forEach((employee) => add(employee, t('matters.referred-by')));
    (matter.employees ?? [])
        .filter((item) => item.matterEmployeeRoleTypeCode === MatterEmployeeRoleType.STAFF)
        .toSorted((a, b) => a.employee.name.localeCompare(b.employee.name))
        .forEach(({ employee, administrator }) =>
            add(employee, caseAssistantLabel && administrator ? caseAssistantLabel : t('matters.handled-by'))
        );
    return [...members.values()];
};
